import React, { useRef, useState } from 'react';
import './MobileCamera.css';

/**
 * Kamera-komponent for mobil befaring
 * Bruker HTML5 Camera API for å ta bilder direkte fra mobil
 * Støtter multiple bilder og automatisk komprimering
 */
function MobileCamera({
  bygningsdel_id,
  onPhotoCaptured,
  onClose,
  minimumPhotos = 2,
  currentPhotoCount = 0
}) {
  const fileInputRef = useRef(null);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Trigger kamera-appen på mobil
  const triggerCamera = () => {
    fileInputRef.current?.click();
  };

  // Håndter bildefangst
  const handleImageCapture = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newPreviews = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(Math.round(((i + 1) / files.length) * 100));

      try {
        // Komprimer bilde før upload
        const compressedFile = await compressImage(file);

        // Lag preview
        const previewUrl = URL.createObjectURL(compressedFile);

        // Ekstraher EXIF-data (GPS, timestamp)
        const exifData = await extractExifData(file);

        newPreviews.push({
          file: compressedFile,
          preview: previewUrl,
          exif: exifData,
          originalName: file.name
        });
      } catch (error) {
        console.error('Feil ved komprimering:', error);
        alert(`Feil ved behandling av bilde ${file.name}`);
      }
    }

    setPreviews(prev => [...prev, ...newPreviews]);
    setUploading(false);
    setUploadProgress(0);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Komprimer bilde til max 1920x1080
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Beregn nye dimensjoner (max 1920x1080)
          let width = img.width;
          let height = img.height;
          const maxWidth = 1920;
          const maxHeight = 1080;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = width * ratio;
            height = height * ratio;
          }

          canvas.width = width;
          canvas.height = height;

          // Tegn komprimert bilde
          ctx.drawImage(img, 0, 0, width, height);

          // Konverter til blob (JPEG, kvalitet 0.85)
          canvas.toBlob(
            (blob) => {
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.\w+$/, '.jpg'),
                { type: 'image/jpeg' }
              );
              resolve(compressedFile);
            },
            'image/jpeg',
            0.85
          );
        };

        img.onerror = reject;
        img.src = e.target.result;
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Ekstraher EXIF-data (GPS, timestamp)
  const extractExifData = async (file) => {
    // Forenklet EXIF-ekstraksjon
    // I produksjon: bruk bibliotek som 'exif-js' eller 'exifreader'
    return {
      timestamp: new Date().toISOString(),
      filename: file.name,
      size: file.size,
      type: file.type
    };
  };

  // Slett preview
  const removePreview = (index) => {
    setPreviews(prev => {
      const newPreviews = [...prev];
      URL.revokeObjectURL(newPreviews[index].preview);
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  // Last opp alle bilder til Cloudflare R2
  const uploadAllPhotos = async () => {
    if (previews.length === 0) {
      alert('Ingen bilder å laste opp');
      return;
    }

    setUploading(true);

    try {
      const uploadedImages = [];

      for (let i = 0; i < previews.length; i++) {
        const preview = previews[i];
        setUploadProgress(Math.round(((i + 1) / previews.length) * 100));

        // Lag FormData for upload
        const formData = new FormData();
        formData.append('file', preview.file);
        formData.append('bygningsdel_id', bygningsdel_id);
        formData.append('exif', JSON.stringify(preview.exif));

        // Last opp til backend
        const response = await fetch('/api/upload-image', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error(`Upload feilet for ${preview.originalName}`);
        }

        const data = await response.json();
        uploadedImages.push({
          url: data.url,
          filename: preview.originalName,
          exif: preview.exif
        });
      }

      // Callback med alle opplastede bilder
      onPhotoCaptured(uploadedImages);

      // Rydd opp previews
      previews.forEach(p => URL.revokeObjectURL(p.preview));
      setPreviews([]);

      alert(`${uploadedImages.length} bilder lastet opp!`);
      onClose();
    } catch (error) {
      console.error('Upload feilet:', error);
      alert('Noe gikk galt ved opplasting. Prøv igjen.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const totalPhotos = currentPhotoCount + previews.length;
  const needsMorePhotos = totalPhotos < minimumPhotos;
  const photosNeeded = Math.max(0, minimumPhotos - totalPhotos);

  return (
    <div className="mobile-camera-overlay">
      <div className="mobile-camera-modal">
        {/* Header */}
        <div className="camera-header">
          <h2>📸 Ta bilder</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Status */}
        <div className={`camera-status ${needsMorePhotos ? 'warning' : 'success'}`}>
          {needsMorePhotos ? (
            <p>⚠️ Trenger minimum {minimumPhotos} bilder (har {totalPhotos})</p>
          ) : (
            <p>✅ Har {totalPhotos} bilder ({minimumPhotos} minimum nådd)</p>
          )}
        </div>

        {/* Kamera-knapp */}
        <div className="camera-trigger">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            onChange={handleImageCapture}
            style={{ display: 'none' }}
          />

          <button
            className="camera-btn-large"
            onClick={triggerCamera}
            disabled={uploading}
          >
            <div className="camera-icon">📷</div>
            <div className="camera-text">
              {previews.length === 0 ? 'Ta bilder' : 'Ta flere bilder'}
            </div>
          </button>

          {needsMorePhotos && (
            <p className="camera-hint">
              Trenger {photosNeeded} flere for TG2/TG3-dokumentasjon
            </p>
          )}
        </div>

        {/* Previews */}
        {previews.length > 0 && (
          <div className="camera-previews">
            <h4>Bilder tatt ({previews.length})</h4>
            <div className="preview-grid">
              {previews.map((preview, idx) => (
                <div key={idx} className="preview-item">
                  <img src={preview.preview} alt={`Forhåndsvisning ${idx + 1}`} />
                  <button
                    className="preview-delete"
                    onClick={() => removePreview(idx)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p>Behandler bilder... {uploadProgress}%</p>
          </div>
        )}

        {/* Actions */}
        <div className="camera-actions">
          {previews.length > 0 && (
            <button
              className="btn-primary large"
              onClick={uploadAllPhotos}
              disabled={uploading}
            >
              ✅ Last opp {previews.length} bilder
            </button>
          )}

          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={uploading}
          >
            {previews.length > 0 ? 'Avbryt' : 'Lukk'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileCamera;
